import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { DailyReportInstitutions, DailyReports } from "@/server/db/types.db";

import { queryKeys } from "./query-keys";

// API response types with timestamps as strings
export type DailyReportItem = Omit<
  DailyReports,
  "created_at" | "updated_at" | "date" | "id" | "expo_version"
> & {
  id: number;
  expo_version: string;
  date: string;
  created_at: string;
  updated_at: string;
};

export type DailyReportInstitutionItem = Omit<
  DailyReportInstitutions,
  "created_at" | "updated_at" | "loan_sales" | "npa_sales" | "id" | "daily_report_id"
> & {
  id: number;
  daily_report_id: number;
  created_at: string;
  updated_at: string;
  loan_sales: string | null;
  npa_sales: string | null;
};

interface DailyReportWithInstitutions extends DailyReportItem {
  institutions: DailyReportInstitutionItem[];
}

interface DailyReportsListResponse {
  reports: DailyReportItem[];
}

interface DailyReportDetailResponse {
  report: DailyReportWithInstitutions;
}

interface CreateDailyReportInput {
  date: string;
  expo_version?: string;
  visitors_count?: number | null;
  stickers_count?: number | null;
  institutions?: Array<{
    institution_name: string;
    loan_units?: number | null;
    loan_sales?: number | null;
    npa_units?: number | null;
    npa_sales?: number | null;
  }>;
}

interface UpdateDailyReportInput {
  visitors_count?: number | null;
  stickers_count?: number | null;
  institutions?: Array<{
    institution_name: string;
    loan_units?: number | null;
    loan_sales?: number | null;
    npa_units?: number | null;
    npa_sales?: number | null;
  }>;
}

// List all daily reports
export function useDailyReports() {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: queryKeys.dailyReports.list,
    queryFn: async () => {
      const response = await authenticatedFetch("/api/admin/daily-reports");

      if (!response.ok) {
        throw new Error(`Failed to fetch daily reports: ${response.statusText}`);
      }

      return response.json() as Promise<DailyReportsListResponse>;
    },
  });
}

// Get single daily report with institutions
export function useDailyReport(id: number | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: id ? queryKeys.dailyReports.detail(id) : ["daily-reports", "detail", "none"],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");

      const response = await authenticatedFetch(`/api/admin/daily-reports/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch daily report: ${response.statusText}`);
      }

      return response.json() as Promise<DailyReportDetailResponse>;
    },
    enabled: !!id,
  });
}

// Create daily report
export function useCreateDailyReport() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDailyReportInput) => {
      const response = await authenticatedFetch("/api/admin/daily-reports", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(
          errorData.message || `Failed to create daily report: ${response.statusText}`
        );
      }

      return response.json() as Promise<DailyReportDetailResponse>;
    },
    onSuccess: () => {
      // Invalidate list query to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.dailyReports.list });
    },
  });
}

// Update daily report
export function useUpdateDailyReport(id: number) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateDailyReportInput) => {
      const response = await authenticatedFetch(`/api/admin/daily-reports/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(
          errorData.message || `Failed to update daily report: ${response.statusText}`
        );
      }

      return response.json() as Promise<DailyReportDetailResponse>;
    },
    onSuccess: () => {
      // Invalidate list and detail queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.dailyReports.list });
      queryClient.invalidateQueries({ queryKey: queryKeys.dailyReports.detail(id) });
    },
  });
}

// Delete daily report
export function useDeleteDailyReport() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await authenticatedFetch(`/api/admin/daily-reports/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(
          errorData.message || `Failed to delete daily report: ${response.statusText}`
        );
      }

      return response.json() as Promise<{ ok: boolean }>;
    },
    onSuccess: () => {
      // Invalidate list query to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.dailyReports.list });
    },
  });
}
